import React, { useLayoutEffect, useRef, useState } from 'react'
import { observer, useLocalObservable } from "mobx-react-lite"
import { useNavigate } from '@reach/router'

import clsx from 'clsx'
import styles from './LoopRow.module.scss'
import IconButton from '@mui/material/IconButton';

import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import CircularProgress from '@mui/material/CircularProgress';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialAction from '@mui/material/SpeedDialAction';

import ShareIcon from '@mui/icons-material/Share';
import ReplyIcon from '@mui/icons-material/Reply';
import Tooltip from '@mui/material/Tooltip';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';

import { getPitchJSX } from '../editor/editorUtils'
import Confirmation from '../../components/Confirmation';
import {StyledMenu, StyledMenuItem} from '../../components/StyledSelect'
import SkeletonRow from './SkeletonRow';
import {ReactComponent as DownloadedIcon} from '../../images/downloaded.svg'
import useWindowSize from '../../hooks/window-size'
import { useTipsEditor } from './ctx'

import EditLoop from './EditLoop'
import Feedback from '../common/Feedback'
import userStore from '../common/userStore'
import ViewLoop from './ViewLoop'
import { shareDialogStore } from '../share/ShareDialog'



const MoreMenu = observer(({model}) => {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { width } = useWindowSize()
  const [openActList, setOpenActList] = useState(false)
  const { canShowTips } = useTipsEditor()

  const toDownload = async() => {
    if (userStore.isAnonymous) {
      userStore.setOpenSignup(true)
      return;
    }
    if (canShowTips) {
      return;
    }
    await model.toDownload()
    setOpenActList(false);
  }

  const openFeedback = () => {
    if (userStore.isAnonymous) {
      userStore.setOpenSignup(true)
      return;
    }
    if (canShowTips) {
      return;
    }
    setOpenActList(false)
    setFeedbackOpen(true)
  }

  const stop = (e) => {
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation()
  }

  if (width <= 500) {
    return <>
      <IconButton className={styles['action-btn']} onClick={() => setOpenActList(true)}>
        <MoreVertIcon />
      </IconButton>
      <SwipeableDrawer open={openActList} anchor="bottom" onClose={() => setOpenActList(false)} onOpen={() => {}} classes={{paper: styles['action-pop-paper']}}>
        <ListItem button onClick={() => shareDialogStore.showModal(model.id)}>
          <ListItemIcon> <ReplyIcon style={{transform: 'scaleX(-1)'}} /> </ListItemIcon>
          <ListItemText primary="Share" />
        </ListItem>
        {/* <Divider /> */}
        <ListItem button onClick={openFeedback}>
          <ListItemIcon> <FeedbackOutlinedIcon /> </ListItemIcon>
          <ListItemText primary="Feedback" />
        </ListItem>
        {/* <Divider /> */}
        <ListItem button disabled={model.downloading} onClick={toDownload}>
          <ListItemIcon> {model.downloading ? <CircularProgress style={{height: 24, width: 24}} /> : (model.downloaded ? <DownloadedIcon /> : <SaveAltIcon />)} </ListItemIcon>
          <ListItemText primary={model.free ? 'Download for free' : (model.downloaded ? "Download again (0 credits)" : "Download and add (1 credit)")} />
        </ListItem>
      </SwipeableDrawer>
      <Feedback open={feedbackOpen} setOpen={setFeedbackOpen} title='Report this loop' appendix={model !== undefined ? 'loop id: ' + model.id: ''}/>
    </>
  }

  return <div className={styles['more-action-btn']}>
    <SpeedDial
        className={styles['more-speed-dial']}
        ariaLabel="more"
        direction={"down"}
        icon={<MoreVertIcon />}
      >
        <SpeedDialAction
            key="k1"
            icon={<ShareIcon />}
            tooltipTitle="Share"
            onClick={() => shareDialogStore.showModal(model.id)}
            onFocus={stop}
          />
        <SpeedDialAction
            key="k2"
            icon={<FeedbackOutlinedIcon />}
            tooltipTitle="Feedback"
            onClick={openFeedback}
            onFocus={stop}
          />
        {width < 768 ? <SpeedDialAction
            key="k3"
            icon={(model.downloaded ? <DownloadedIcon /> : <SaveAltIcon />)}
            tooltipTitle="Download"
            onClick={toDownload}
            onFocus={stop}
          /> : null}
      </SpeedDial>
      <Feedback open={feedbackOpen} setOpen={setFeedbackOpen} title='Report this loop' appendix={model !== undefined ? 'loop id: ' + model.id: ''}/>
  </div>
})

const PlayButton = observer(({className, model}) => {
  return <IconButton className={className} color="primary">
    {model.pending ? <CircularProgress style={{height: 24, width: 24}} /> : (model.playing ? <StopCircleOutlinedIcon /> : <PlayCircleOutlineIcon />)}
  </IconButton>
})

const RemoveButton = observer(({model}) => {
  const [open, setOpen] = useState(false)
  return <>
    <IconButton className={styles['action-btn']} onClick={() => setOpen(true)}><DeleteOutlineIcon /></IconButton>
    <Confirmation open={open} setOpen={setOpen} onSubmit={model.removeMyLoop} 
      content={<span className="defaultFont">Are you sure you want to permanently delete this loop?</span>} />
  </>
})

const StateAction = observer(({model}) => {
  const { canShowTips } = useTipsEditor()
  const addToMyLoop = () => {
    if (canShowTips) {
      return;
    }
    model.addToMyLoop()
  }
  if (model.isMy) {
    return <RemoveButton model={model} />
  }
  if (model.hasToMyloops) {
    return <IconButton className={clsx(styles['action-btn'], styles['add-action'])}><CheckCircleIcon /></IconButton>
  }
  return <Tooltip title="Add to my loops" enterDelay={500} arrow placement="top" PopperProps={{modifiers: [{name: 'offset', options: {offset: [0, -7]}}]}}>
    <IconButton className={clsx(styles['action-btn'], styles['add-action'])} onClick={addToMyLoop}><AddCircleOutlineIcon /></IconButton></Tooltip>
})

const DownloadAction = observer(({model}) => {
  const { canShowTips } = useTipsEditor()
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const toDownload = (midi = 0) => {
    if (canShowTips) {
      return;
    }
    model.toDownload(midi)
  }
  return (model.downloading ? <IconButton className={clsx(styles['action-btn'], styles['download-action'])}><CircularProgress style={{height: 24, width: 24}} /></IconButton>
    : <Tooltip title={model.free ? 'Download for free' : (model.downloaded ? "Download again (0 credits)" : "Download (1 credit)")} enterDelay={500} arrow placement="top" PopperProps={{modifiers: [{name: 'offset', options: {offset: [0, -7]}}]}}>
    <IconButton className={clsx(styles['action-btn'], styles['download-action'])} onClick={e => model.midi ? setDownloadAnchorEl(Boolean(downloadAnchorEl) ? null : e.currentTarget) : toDownload(0)}>
      {model.downloaded ? <DownloadedIcon /> : <SaveAltIcon />}
      <StyledMenu 
        style={{transform: 'translateY(-13px)'}}
        id="download-menu"
        anchorEl={downloadAnchorEl}
        open={Boolean(downloadAnchorEl)}
        onClose={() => setDownloadAnchorEl(null)}
        transitionDuration={{ appear:150, enter: 150, exit: 0 }}
      >
          <StyledMenuItem style={{paddingLeft: 8}} onClick={() => { setDownloadAnchorEl(null); toDownload(0)}}>
            <ListItemIcon><GraphicEqIcon fontSize="small" /></ListItemIcon>Audio
          </StyledMenuItem>
          <StyledMenuItem style={{paddingLeft: 2}} onClick={() => { setDownloadAnchorEl(null); toDownload(1)}}>
            <ListItemIcon><MusicNoteIcon fontSize="small" /></ListItemIcon>MIDI
          </StyledMenuItem>
      </StyledMenu>
    </IconButton></Tooltip>
  );
  
  if (model.downloading) {
    return <IconButton className={clsx(styles['action-btn'], styles['download-action'])}><CircularProgress style={{height: 24, width: 24}} /></IconButton>
  }
  if (model.downloaded) {
    return <Tooltip title="Download again (0 credits)" enterDelay={500} arrow placement="top" PopperProps={{modifiers: [{name: 'offset', options: {offset: [0, -7]}}]}}>
      <IconButton className={clsx(styles['action-btn'], styles['download-action'])} onClick={toDownload}><DownloadedIcon /></IconButton></Tooltip>
  }
  return <Tooltip title="Download and add (1 credit)" enterDelay={500} arrow placement="top" PopperProps={{modifiers: [{name: 'offset', options: {offset: [0, -7]}}]}}>
    <IconButton className={clsx(styles['action-btn'], styles['download-action'])} onClick={toDownload}><SaveAltIcon /></IconButton></Tooltip>
})

const LoopRow = observer(({index, model, showCover}) => {
  const rowClz = index % 2 === 0 ? styles['even'] : styles['odd']
  const background = index % 2 === 0 ? '#FFF' : '#F2F2F2'
  const domRef = useRef(null)
  const navigate = useNavigate()
  const stop = (e) => e.stopPropagation()
  const { canShowTips } = useTipsEditor()

  useLayoutEffect(() => {
    model.setDom(domRef.current)
  }, [domRef.current])

  const toPlay = () => {
    if (canShowTips) {
      return;
    }
    model.togglePlaying()
  }

  const toPack = (e) => {
    e.stopPropagation()
    if (canShowTips) {
      return;
    }
    navigate(`/pack/${model.loop.packUrl || model.packId}`)
  }

  if (model.parent.loading || model.mock) {
    return <SkeletonRow index={index} model={model} showCover={showCover} />
  }

  return <div ref={domRef} className={clsx(styles['root'], rowClz, {[styles['active']]: model.active, [styles['playing']]: model.playing})} onClick={toPlay}>
    {showCover ? <div className={styles['cover']} onClick={toPack}>
      <div className={styles['cover-img']} style={{backgroundImage: `url(${model.thumbnail})`, width: 32, height: 32}}></div>
    </div> : null}
    <div className={showCover ? styles['bars-cover'] : styles['bars']}>
      <div className={styles['play']}>
        <PlayButton className="play-btn" model={model} />
        <span className="no1">{model.bars}</span>
      </div>
    </div>
    <div className={styles['process']}>
      <div className={styles['wave']}>
        <EditLoop model={model} background={background} first={index == 1} />
      </div>
    </div>
    <div className={styles['name']}>
      {model.name}
    </div>
    <div className={styles['modified']}>
      {model.modifyTime}
    </div>
    <div className={styles['key']}>
      {getPitchJSX(model.key)}{model.mode === 'major' ? '' : 'm'}
    </div>
    <div className={styles['bpm']}>
     {model.bpm}
    </div>
    <div className={clsx(styles['action'], 'loops-row-action')} onClick={stop}>

      <StateAction model={model} />
      <ViewLoop model={model} first={index == 1} />
      <DownloadAction model={model} />
      <MoreMenu model={model} />
    </div>
  </div>
})

export default LoopRow